import { useNavigate } from "react-router-dom";
import { ShoppingCart, ArrowRight, ShieldCheck, Usb } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useCartStore } from "@/stores/cartStore";
import { formatCurrency } from "@/api/client";

const kindLabel: Record<string, string> = {
  ECPF: "e-CPF",
  ECNPJ: "e-CNPJ",
  EMEI: "e-MEI"
};

function buildProductLabel(item: ReturnType<typeof useCartStore.getState>["lastAddedProduct"]) {
  if (!item) return "";

  if (item.productType === "TOKEN") {
    return "Token USB ICP-Brasil";
  }

  const kind = item.certificateKind ? kindLabel[item.certificateKind] ?? item.certificateKind : "Certificado";
  const type = item.certificateType ?? "";

  return `${kind} ${type}`.trim();
}

export function CartAddedModal() {
  const navigate = useNavigate();
  const { showCartModal, lastAddedProduct, closeCartModal, totalItems } = useCartStore();

  const label = buildProductLabel(lastAddedProduct);
  const isToken = lastAddedProduct?.productType === "TOKEN";

  function handleContinue() {
    closeCartModal();
  }

  function handleCheckout() {
    closeCartModal();
    navigate("/checkout");
  }

  return (
    <Dialog open={showCartModal} onOpenChange={(open) => { if (!open) closeCartModal(); }}>
      <DialogContent className="max-w-sm rounded-[1.75rem] p-0 overflow-hidden gap-0 border-0 shadow-2xl">

        {/* Header */}
        <div className={`px-6 pt-7 pb-5 flex flex-col items-center text-center ${isToken ? "bg-footer" : "bg-primary"}`}>
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15 mb-4">
            {isToken
              ? <Usb className="h-7 w-7 text-white" />
              : <ShieldCheck className="h-7 w-7 text-white" />
            }
          </div>
          <p className="text-xs font-semibold uppercase tracking-widest text-white/60 mb-1">
            Adicionado ao carrinho
          </p>
          <p className="text-2xl font-bold text-white leading-tight">
            {label}
          </p>
        </div>

        {/* Price */}
        {lastAddedProduct && (
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <span className="text-sm text-muted-foreground">Valor</span>
            <span className="text-xl font-bold text-foreground">
              {formatCurrency(lastAddedProduct.priceCents)}
            </span>
          </div>
        )}

        {/* Cart summary */}
        <div className="flex items-center justify-between px-6 py-3 bg-muted/50">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <ShoppingCart className="h-4 w-4" />
            <span>Itens no carrinho</span>
          </div>
          <span className="text-sm font-semibold text-foreground">{totalItems()}</span>
        </div>

        {/* Actions */}
        <div className="grid grid-cols-2 gap-3 px-6 py-5">
          <Button
            variant="outline"
            className="rounded-xl h-11"
            onClick={handleContinue}
          >
            Continuar
          </Button>
          <Button
            className="rounded-xl h-11 gap-2 bg-success hover:bg-success/90"
            onClick={handleCheckout}
          >
            Pagar agora
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>

      </DialogContent>
    </Dialog>
  );
}
